/**
 * MeetingActionModal — unified Log / Schedule meeting flow for Contact page.
 *
 * Two tabs:
 *   1. Log: log a past meeting as an Activity on the contact timeline.
 *   2. Schedule: provider-native scheduling (issue #503) — pick the calendar
 *      (Google / Outlook), a conference option only when the selected calendar
 *      can create one (issue #513: Google → Meet; #514: Outlook → Teams), and one long
 *      invitation message that becomes the event description. The calendar
 *      provider itself sends the ONE invitation (from the user's own address,
 *      carrying the .ics) — no composed email, no template/signature pickers,
 *      no noreply@ relay.
 *
 * Both tabs used to POST to `/meeting-sessions` first and read `session_id`
 * off the reply. Meeting Copilot was extracted to a module provider and that
 * route now answers 410, which axios raises — so BOTH flows failed outright
 * before ever reaching the activity write, and the Log tab could not log a
 * meeting at all. The session hop and the playbook picker (`/playbooks`, also
 * 410) are gone; everything the runtime still owns — the activity, the calendar
 * event — is unchanged.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
    Calendar as CalendarIcon, CalendarCheck, Loader2, Sparkles, Send,
} from 'lucide-react';
import { useScheduleCapabilities, SCHEDULE_DISABLED_COPY } from '../hooks/useScheduleCapabilities';
import CalendarPicker from './schedule/CalendarPicker';
import LocationPicker from './schedule/LocationPicker';
import { buildLocationOptions, conferenceFlags, locationLabel } from './schedule/locationOptions';
import { calendarWriteWarning } from '../lib/calendarWarnings';
import { applyMerge } from '../lib/mergeVars';
import { useAuth } from '../context/AuthContext';
import TimezoneSelect from './TimezoneSelect';
import { browserTimeZone, resolveDisplayZone } from '../lib/timezone';
import { errorText } from '../lib/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function todayYMD() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function MeetingActionModal({
    open,
    onOpenChange,
    contact,
    account,
    deal,
    defaultTab = 'schedule',
    onDone,
    getAuthHeader,
    readOnlyReason = null,
}) {
    const [tab, setTab] = useState(defaultTab);
    useEffect(() => { if (open) setTab(defaultTab); }, [open, defaultTab]);

    const contactName = useMemo(() => {
        if (!contact) return '';
        return [contact.first_name, contact.last_name].filter(Boolean).join(' ');
    }, [contact]);

    // ───────── shared shape ─────────
    const [title, setTitle] = useState('');
    const [date, setDate] = useState(todayYMD());
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('09:30');
    const [notes, setNotes] = useState('');

    // ───────── Schedule-only state ─────────
    const [selectedCalendar, setSelectedCalendar] = useState(null);
    const [location, setLocation] = useState('google_meet');
    const [invitationMessage, setInvitationMessage] = useState('');

    // #537: the zone the naive start/end wall-clock is authored IN. Chain per
    // Epic #532: contact.timezone → user profile → browser → UTC. The modal
    // receives the user profile via useAuth (the getAuthHeader prop stays the
    // auth-token seam; this read is display-only).
    const { user } = useAuth();
    const [selectedTimezone, setSelectedTimezone] = useState(null);
    useEffect(() => {
        if (!open) return;
        setSelectedTimezone(resolveDisplayZone({ contact, user }));
    }, [open, contact, user]);

    const [aiRewriting, setAiRewriting] = useState(false);
    const [saving, setSaving] = useState(false);
    const queryClient = useQueryClient();

    // Calendar capabilities (issue #503): the invitation is sent by the
    // calendar provider itself, so scheduling is gated on a connected
    // CALENDAR — not on a mailbox. useScheduleCapabilities composes
    // useCalendarStatus (Google) + useOutlookCalendarStatus (Outlook with
    // calendar scope); do NOT read useCalendarStatus().outlook (hardcoded
    // 'disconnected' stub) or useOutlookStatus (mailbox-only signal).
    const { calendars, anyCalendarConnected } = useScheduleCapabilities();


    // Default the calendar selection: only one → it; both → Google (first
    // available). Re-pick whenever the connected set changes so a calendar
    // disconnecting mid-session never leaves a stale selection.
    useEffect(() => {
        if (!open || calendars.length === 0) return;
        setSelectedCalendar((prev) => {
            if (prev && calendars.some((c) => c.value === prev)) return prev;
            if (calendars.some((c) => c.value === 'google')) return 'google';
            return calendars[0].value;
        });
    }, [open, calendars]);

    // Seed the invitation message when the contact changes (only when empty —
    // never overwrite user edits). Its content becomes the calendar event
    // description, which the provider's invitation carries as the message body.
    useEffect(() => {
        if (!open || tab !== 'schedule') return;
        if (invitationMessage) return;
        const mergeVars = {
            contact_first_name: contact?.first_name || '',
            contact_full_name: contactName,
            company_name: account?.name || '',
        };
        setInvitationMessage(applyMerge(
            'Hi {{contact_first_name}},\n\nLooking forward to our meeting. Agenda and details below.\n\nBest,',
            mergeVars
        ));
    }, [open, tab, contact, account, contactName, invitationMessage]);

    // AI rewrite — reuse the existing /api/ai/write-email endpoint
    const aiRewrite = useCallback(async () => {
        setAiRewriting(true);
        try {
            const res = await axios.post(
                `${API}/ai/write-email`,
                {
                    existing_draft: invitationMessage || '',
                    prompt: invitationMessage
                        ? 'Rewrite this meeting invitation to be warmer, clearer, and concise.'
                        : `Write a meeting invitation to ${contactName || 'the contact'} about the upcoming meeting titled "${title}".`,
                    contact_id: contact?.contact_id,
                    account_id: account?.account_id,
                    deal_id: deal?.deal_id,
                    agent_type: 'email_writer',
                    scope: 'full',
                },
                { headers: getAuthHeader() }
            );
            const rewritten = res.data?.body || res.data?.content || res.data?.text || '';
            if (!rewritten) throw new Error('AI returned empty response');
            setInvitationMessage(rewritten);
            toast.success('AI rewrite complete');
        } catch (err) {
            toast.error(errorText(err, err.message || 'AI rewrite failed'));
        } finally {
            setAiRewriting(false);
        }
    }, [invitationMessage, contactName, title, contact, account, deal, getAuthHeader]);

    // Location options depend on the SELECTED calendar (issues #503/#513/#514):
    //   Google  → Google Meet (minted server-side by Google)
    //   Outlook → Microsoft Teams (minted by Graph on insert)
    // Both also offer "No conference link" (#514 audit A03) — the only path a
    // Microsoft account whose licence disallows Teams has to Outlook at all.
    // Phone / In person are withdrawn behind WITHDRAWN_LOCATIONS_RESTORED.
    // Zoom was retired 2026-09-04 (#513).
    const locationOptions = useMemo(() => buildLocationOptions(selectedCalendar), [selectedCalendar]);

    // When the selected calendar changes, drop a location that is no longer
    // valid — e.g. the user picked Outlook while google_meet was selected.
    useEffect(() => {
        setLocation((prev) => (locationOptions.some((o) => o.value === prev)
            ? prev
            : locationOptions[0]?.value));
    }, [locationOptions]);

    // ─────────────────────────────────────────────────────────────
    // Submit — Log (submitRecord identifier kept for compatibility)
    // ─────────────────────────────────────────────────────────────
    const submitRecord = async () => {
        if (readOnlyReason) { toast.error(readOnlyReason); return; }
        if (!title.trim()) { toast.error('Title required'); return; }
        setSaving(true);
        try {
            // Log as an Activity on the contact timeline. This is the whole
            // operation now — see the header note on the removed session hop.
            await axios.post(`${API}/activities`, {
                type: 'meeting',
                title,
                description: notes,
                contact_id: contact?.contact_id,
                account_id: account?.account_id,
                deal_id: deal?.deal_id,
                completed_at: `${date}T${startTime}:00`,
            }, { headers: getAuthHeader() }).catch(err => {
                toast.error(errorText(err, 'Meeting saved but the timeline activity failed to persist'));
                throw err;
            });

            toast.success('Meeting logged');
            onDone?.({ kind: 'recorded' });
            onOpenChange(false);
        } catch (err) {
            toast.error(errorText(err, 'Failed to log meeting'));
        } finally {
            setSaving(false);
        }
    };

    // Single activity writer for the schedule path. Resolves true when the
    // activity persisted; false after surfacing the failure itself (issue
    // #458: the meeting body + scheduled_at live in this activity — the
    // failure is never swallowed, and the caller never toasts success on top
    // of it).
    const writeActivity = async ({ inviteStatus, event }) => {
        try {
            await axios.post(`${API}/activities`, {
                type: 'meeting',
                title,
                description: invitationMessage,
                contact_id: contact?.contact_id,
                account_id: account?.account_id,
                deal_id: deal?.deal_id,
                scheduled_at: `${date}T${startTime}:00`,
                invite_status: inviteStatus,
                calendar_event_id: event?.crm_event_id || event?.event_id || null,
            }, { headers: getAuthHeader() });
            return true;
        } catch (err) {
            toast.error(errorText(err, 'Meeting scheduled but the timeline activity failed to persist'));
            return false;
        }
    };

    // ─────────────────────────────────────────────────────────────
    // Submit — Schedule
    // ─────────────────────────────────────────────────────────────
    const submitSchedule = async () => {
        if (readOnlyReason) { toast.error(readOnlyReason); return; }
        if (!title.trim()) { toast.error('Title required'); return; }
        if (!date) { toast.error('Date required'); return; }
        if (!anyCalendarConnected) {
            // Defense in depth — the toolbar button is the primary gate and
            // the backend's 400 "No calendar connected" is the real control.
            toast.error(SCHEDULE_DISABLED_COPY);
            return;
        }
        setSaving(true);
        try {
            // 1. Create the event in the SELECTED calendar only (issue #503).
            //    Field names are the backend's accepted keys (issue #452 fix):
            //    title/description/start/end/add_google_meet — NOT
            //    summary/start_time/end_time/add_meet. No contact_ids: the
            //    backend would write its own activity; the modal writes the
            //    single activity itself (step 2).
            const startDateTime = `${date}T${startTime}:00`;
            const endDateTime = `${date}T${endTime}:00`;
            const eventLocation = locationLabel(location);
            const eventPayload = {
                title,
                description: invitationMessage,
                location: eventLocation,
                start: startDateTime,
                end: endDateTime,
                // #514: both conference flags derive from the (calendar,
                // location) pair — never set alone (shared rule, #680).
                ...conferenceFlags(selectedCalendar, location),
                attendees: contact?.email ? [contact.email] : [],
                calendar: selectedCalendar,
                // #537: the naive start/end strings above are authored in this
                // zone; the backend converts them to UTC and persists it as
                // display_timezone. Omitted on legacy callers → UTC default.
                timezone: selectedTimezone || browserTimeZone(),
            };

            let event = null;
            let inviteStatus = 'not_invited';
            try {
                const res = await axios.post(`${API}/oauth/calendar/events`, eventPayload, { headers: getAuthHeader() });
                event = res.data;
                // partial_success means the SELECTED provider failed to write
                // (a retry is enqueued server-side) — never claim the invite
                // went out.
                inviteStatus = res.data.partial_success ? 'not_invited' : 'invited';
                // Mirror useCreateCalendarEvent's invalidation (useCalendar.js).
                queryClient.invalidateQueries({ queryKey: ['calendar', 'aggregated'] });
            } catch (calErr) {
                // Surface the failure — never a bare "Meeting scheduled". The
                // attempt is still logged, marked not_invited, with no event
                // link. The Teams-refusal 422 (#514 D1) carries copy the user
                // can act on — show it verbatim; the generic sentence would
                // tell them nothing about WHY it failed.
                toast.error(
                    calErr.response?.data?.detail
                    || 'The calendar invitation could not be created. The meeting was not scheduled.'
                );
                await writeActivity({ inviteStatus: 'not_invited', event: null });
                onDone?.({ kind: 'schedule_failed' });
                return;
            }

            // 2. Log EXACTLY ONE activity on the contact timeline, carrying
            //    the invite status and the event link (Phase 2 model fields).
            const persisted = await writeActivity({ inviteStatus, event });
            if (!persisted) return; // activity failure already surfaced

            if (inviteStatus === 'invited') {
                toast.success('Meeting scheduled — invitation sent from your calendar.');
            } else {
                // A permanent Graph rejection (#514 F-2) enqueues nothing
                // server-side, so promising a retry there would be false. That
                // decision is shared with useCreateCalendarEvent via
                // lib/calendarWarnings.js (#514 audit A10); only the noun
                // differs — this surface scheduled a MEETING.
                toast.warning(calendarWriteWarning(event, { variant: 'meeting' }));
            }
            onDone?.({ kind: 'scheduled' });
            onOpenChange(false);
        } catch (err) {
            toast.error(errorText(err, 'Failed to schedule meeting'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-4xl max-h-[90vh] overflow-y-auto"
                data-testid="meeting-action-modal"
            >
                <DialogHeader>
                    <DialogTitle>
                        {tab === 'record' ? 'Log a past meeting' : 'Schedule a meeting'}
                    </DialogTitle>
                </DialogHeader>

                {/* Tabs */}
                <div className="flex items-center bg-stone-100 rounded-lg p-0.5 w-fit mb-4">
                    <button
                        onClick={() => setTab('record')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            tab === 'record' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                        }`}
                        data-testid="tab-record"
                    >
                        <CalendarCheck className="w-3.5 h-3.5" /> Log meeting
                    </button>
                    <button
                        onClick={() => setTab('schedule')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            tab === 'schedule' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                        }`}
                        data-testid="tab-schedule"
                    >
                        <CalendarIcon className="w-3.5 h-3.5" /> Schedule meeting
                    </button>
                </div>

                {/* Shared fields */}
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Title</Label>
                        <Input value={title} onChange={e => setTitle(e.target.value)}
                            placeholder="Discovery call, QBR, demo…" />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs">Date</Label>
                            <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
                        </div>
                        <div>
                            <Label className="text-xs">Start time</Label>
                            <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
                        </div>
                        <div>
                            <Label className="text-xs">End time</Label>
                            <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
                        </div>
                    </div>

                    {tab === 'record' ? (
                        <div>
                            <Label className="text-xs">Notes</Label>
                            <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                                rows={6} placeholder="What was discussed, outcomes, next steps…" />
                        </div>
                    ) : (
                        <>
                            {/* #537: zone the wall-clock above is authored in —
                                contact → profile → browser → UTC (Epic #532). */}
                            <div>
                                <Label className="text-xs" htmlFor="meeting-timezone">Timezone</Label>
                                <TimezoneSelect
                                    id="meeting-timezone"
                                    value={selectedTimezone || browserTimeZone()}
                                    onChange={setSelectedTimezone}
                                />
                            </div>

                            {/* Calendar picker — 0 / 1 / many (issue #503) */}
                            <CalendarPicker
                                options={calendars}
                                value={selectedCalendar}
                                onChange={setSelectedCalendar}
                            />

                            {/* Location — 0 / 1 / many, mirroring CalendarPicker (issues #513/#514) */}
                            <LocationPicker
                                options={locationOptions}
                                value={location}
                                onChange={setLocation}
                                calendarLabel={calendars.find(c => c.value === selectedCalendar)?.label}
                            />

                            {/* Invitation message — ONE long text field whose
                                content becomes the event description the
                                provider's invitation carries (issue #503). */}
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs">Invitation message</Label>
                                    <button
                                        type="button"
                                        onClick={aiRewrite}
                                        disabled={aiRewriting}
                                        className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                                        data-testid="ai-rewrite-invitation"
                                    >
                                        {aiRewriting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                        AI rewrite
                                    </button>
                                </div>
                                <Textarea
                                    value={invitationMessage}
                                    onChange={e => setInvitationMessage(e.target.value)}
                                    rows={10}
                                    placeholder="Hi Fermin, looking forward to our meeting. Agenda and details below…"
                                    className="text-sm"
                                />
                            </div>

                            {/* Footer status line (wireframes B2/B3) */}
                            <div className="text-[11px]">
                                {selectedCalendar ? (
                                    <span className="inline-flex items-center gap-1 text-green-600">
                                        <span className="w-2 h-2 rounded-full bg-green-500" />
                                        The event will be created in your {calendars.find(c => c.value === selectedCalendar)?.label} calendar, and the invitation sent from your own address with an .ics your contact can accept in Google Calendar or Outlook.
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-amber-600">
                                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                                        {SCHEDULE_DISABLED_COPY}
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-stone-200">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancel
                    </Button>
                    {tab === 'record' ? (
                        <Button onClick={submitRecord} disabled={saving || !!readOnlyReason} title={readOnlyReason || undefined} data-testid="submit-record">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CalendarCheck className="w-4 h-4 mr-1.5" />}
                            Log meeting
                        </Button>
                    ) : (
                        <div className="flex flex-col items-end gap-1">
                            {/* Defense in depth: aria-disabled (not native
                                disabled) when no calendar is connected — the
                                click still lands so the helper text explains
                                why, and the backend 400 remains the real
                                gate. */}
                            <Button
                                onClick={submitSchedule}
                                aria-disabled={!anyCalendarConnected || !!readOnlyReason}
                                disabled={saving || !!readOnlyReason} title={readOnlyReason || undefined}
                                className={anyCalendarConnected ? undefined : 'opacity-50 cursor-not-allowed'}
                                data-testid="submit-schedule"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                                Schedule
                            </Button>
                            {!anyCalendarConnected && (
                                <span className="text-[10px] text-amber-600 max-w-[220px] text-right">
                                    {SCHEDULE_DISABLED_COPY}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}